import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const MESSAGES = [
  "The veil thins at 11:11. Be seated before it does.",
  "Look inside the red envelope first. Ignore the knocking.",
  "Three knocks will come. Answer only the third.",
  "The flame leans toward the north window. Follow it.",
  "Speak the name written beneath the wax. Speak it kindly.",
];

export const Ritual = () => {
  const [burst, setBurst] = useState(0);
  const [msgIndex, setMsgIndex] = useState(-1);

  const commune = () => {
    setMsgIndex((i) => (i + 1) % MESSAGES.length);
    setBurst((b) => b + 1);
  };

  return (
    <section
      id="ritual"
      data-testid="ritual-section"
      className="relative border-y border-gold/10 bg-surface/40 py-24 sm:py-36"
    >
      <div className="mx-auto flex max-w-3xl flex-col items-center px-6 text-center">
        <Reveal>
          <p className="font-cinzel text-[11px] tracking-[0.45em] text-gold/80">
            EPISODE ONE — A TASTE OF THE RITUAL
          </p>
          <h2 className="mt-6 font-serif text-3xl font-light text-cream sm:text-5xl">
            Commune with the <em className="italic text-gold">house spirit.</em>
          </h2>
          <p className="mt-6 text-sm leading-relaxed text-sand sm:text-base">
            The Séance carries a resident presence. Tap the seal below and receive
            a communiqué — a fragment of what awaits inside Episode One.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-16">
          <div className="relative flex h-48 w-48 items-center justify-center">
            {burst > 0 &&
              [...Array(16)].map((_, i) => {
                const angle = (i / 16) * Math.PI * 2;
                return (
                  <motion.span
                    key={`${burst}-${i}`}
                    className="absolute left-1/2 top-1/2 h-1 w-1 rounded-full bg-gold-glow"
                    initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                    animate={{
                      x: Math.cos(angle) * 100,
                      y: Math.sin(angle) * 100,
                      opacity: 0,
                      scale: 0.2,
                    }}
                    transition={{ duration: 1, ease: "easeOut" }}
                  />
                );
              })}
            <motion.button
              data-testid="ritual-seal-button"
              onClick={commune}
              whileHover={{ scale: 1.06 }}
              whileTap={{ scale: 0.94 }}
              className="relative flex h-28 w-28 items-center justify-center rounded-full bg-crimson shadow-[0_0_60px_rgba(139,30,43,0.5),inset_0_4px_12px_rgba(0,0,0,0.5)] transition-shadow duration-500 hover:shadow-[0_0_90px_rgba(139,30,43,0.8),inset_0_4px_12px_rgba(0,0,0,0.5)]"
              aria-label="Tap the wax seal to commune"
            >
              <span className="absolute inset-2 rounded-full border border-cream/20" />
              <span className="font-cinzel text-3xl text-cream/90">S</span>
            </motion.button>
          </div>
        </Reveal>
        <div className="mt-12 flex min-h-[7rem] items-center justify-center">
          <AnimatePresence mode="wait">
            {msgIndex >= 0 && (
              <motion.div
                key={msgIndex}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              >
                <p
                  data-testid="ritual-message"
                  className="max-w-md font-serif text-xl italic leading-relaxed text-gold-glow sm:text-2xl"
                >
                  “{MESSAGES[msgIndex]}”
                </p>
                <p
                  data-testid="ritual-counter"
                  className="mt-4 font-cinzel text-[10px] tracking-[0.4em] text-ash"
                >
                  COMMUNIQUÉ Nº {String(msgIndex + 1).padStart(2, "0")} /{" "}
                  {String(MESSAGES.length).padStart(2, "0")}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
